// =====================================================
// TYPES: TIMELINE
// Description: Types TypeScript pour le système d'historique unifié
// =====================================================

/**
 * Types d'événements de timeline (enum-like object)
 */
export const TIMELINE_EVENT_TYPE = {
  EMAIL_SENT: 'email_sent',
  EMAIL_RECEIVED: 'email_received',
  // Email non délivré : hard bounce / blocked / spam Mailjet (TICKET-CRM-13, migration 00158)
  EMAIL_BOUNCED: 'email_bounced',
  CALL_LOGGED: 'call_logged',
  STATUS_CHANGED: 'status_changed',
  ASSIGNED_USER_CHANGED: 'assigned_user_changed',
  NOTE_CREATED: 'note_created',
  ENTITY_CREATED: 'entity_created',
  TASK_CREATED: 'task_created',
  TASK_COMPLETED: 'task_completed',
  TASK_RESCHEDULED: 'task_rescheduled',
  SEQUENCE_SENT: 'sequence_sent',
  DATA_UPDATED: 'data_updated',
  CREW_MEMBER_ADDED: 'crew_member_added',
  CREW_MEMBER_REMOVED: 'crew_member_removed',
  PAYMENT_INITIATED: 'payment_initiated',
  PAYMENT_METHOD_SELECTED: 'payment_method_selected',
  PAYMENT_METHOD_CHANGED: 'payment_method_changed',
  PAYMENT_PAID: 'payment_paid',
  PAYMENT_FAILED: 'payment_failed',
  // Paiement BED reçu par l'IPN du site sans tentative en attente : à
  // rapprocher à la main (4LT /api/payment-confirm, migration 00319)
  PAYMENT_ATTEMPT_UNTRACKED: 'payment_attempt_untracked',
  PAYMENT_PAYER_IDENTIFIED: 'payment_payer_identified',
  PAYMENT_PAYER_CHANGED: 'payment_payer_changed',
  PAYMENT_REMINDER_SENT: 'payment_reminder_sent',
  // Renvoi manuel du mail de paiement BPI (TICKET-CRM-10, migration 00134)
  PAYMENT_LINK_RESENT: 'payment_link_resent',
  // Attestation « BPI réglé » posée à la main par le staff, sans encaissement
  // dans le CRM (T1, RPC validate_bpi_manually, migrations 00225/00226)
  BPI_MANUALLY_VALIDATED: 'bpi_manually_validated',
  // Émission à froid d'un lien de paiement BED (RPC create_bed_payment_case, 00130)
  BED_LINK_GENERATED: 'bed_link_generated',
  // Expiration d'un lien BED (TTL bed_token dépassé)
  BED_LINK_EXPIRED: 'bed_link_expired',
  // Envoi effectif du mail contenant le lien BED (migration 00136)
  BED_LINK_SENT: 'bed_link_sent',
  // Sélection d'options facultatives par le participant sur le tunnel BED
  // (RPC set_bed_case_options, migrations 00163/00164)
  BED_OPTIONS_UPDATED: 'bed_options_updated',
  // Montant du solde BED réaligné sur le total vivant après une remise, une
  // grille reconfirmée ou un envoi du bulletin (migration 00280)
  BED_AMOUNT_ADJUSTED: 'bed_amount_adjusted',
  // Montant du solde BED à surveiller : ajustement en attente d'un paiement CB,
  // ajustement impossible, dossier soldé sous ou au-dessus du total (00280)
  BED_AMOUNT_ALERT: 'bed_amount_alert',
  // Remboursement d'un trop-perçu noté par le staff (fait à la main hors CRM ;
  // RPC record_payment_refund, migration 00295)
  PAYMENT_REFUND_RECORDED: 'payment_refund_recorded',
  // Émission du lien du FORMULAIRE BED depuis la fiche équipage (RPC
  // mint_bed_form_link, 00232). À ne pas confondre avec BED_LINK_GENERATED,
  // qui est le lien de PAIEMENT direct.
  BED_FORM_LINK_GENERATED: 'bed_form_link_generated',
  // Levée (ou annulation de levée) manuelle d'un élément manquant par
  // l'administration (RPC bed_resolve_missing_item, 00232)
  BED_MISSING_ITEM_RESOLVED: 'bed_missing_item_resolved',
  BED_RESUBMITTED: 'bed_resubmitted',
  BED_CERTIFICATE_REVIEWED: 'bed_certificate_reviewed',
  BED_CUSTOM_ITEM_ADDED: 'bed_custom_item_added',
  BED_CUSTOM_ITEM_REMOVED: 'bed_custom_item_removed',
  BED_REMINDER_QUEUED: 'bed_reminder_queued',
  BED_VEHICLE_ATTACHED: 'bed_vehicle_attached',
  BED_VEHICLE_REFUSED: 'bed_vehicle_refused',
  // @deprecated - Utiliser DATA_UPDATED à la place
  FIELD_UPDATED: 'field_updated',
} as const;

export type TimelineEventType = (typeof TIMELINE_EVENT_TYPE)[keyof typeof TIMELINE_EVENT_TYPE];

/**
 * Types d'entités
 */
export const ENTITY_TYPE = {
  CONTACT: 'contact',
  CREW: 'crew',
  PROSPECT: 'prospect',
} as const;

export type EntityType = (typeof ENTITY_TYPE)[keyof typeof ENTITY_TYPE];

/**
 * Directions d'appel
 */
export const CALL_DIRECTION = {
  INCOMING: 'incoming',
  OUTGOING: 'outgoing',
} as const;

export type CallDirection = (typeof CALL_DIRECTION)[keyof typeof CALL_DIRECTION];

/**
 * Issues d'appel
 */
export const CALL_OUTCOME = {
  ANSWERED: 'answered',
  VOICEMAIL: 'voicemail',
  NO_ANSWER: 'no_answer',
} as const;

export type CallOutcome = (typeof CALL_OUTCOME)[keyof typeof CALL_OUTCOME];

// =====================================================
// MÉTADONNÉES SPÉCIFIQUES PAR TYPE D'ÉVÉNEMENT
// =====================================================

export interface CallLoggedMetadata {
  direction: CallDirection;
  outcome: CallOutcome;
  duration?: number; // en minutes, 0 pour répondeur
  notes?: string;
}

export interface EmailSentMetadata {
  to: string[];
  cc?: string[];
  subject: string;
  body: string;
  template_id?: string;
}

export interface StatusChangedMetadata {
  old_status: string;
  new_status: string;
}

export interface AssignedUserChangedMetadata {
  old_user_id: string | null;
  old_user_name: string | null;
  new_user_id: string | null;
  new_user_name: string | null;
}

export interface FieldUpdatedMetadata {
  field: string;
  field_label: string;
  old_value: any;
  new_value: any;
}

export interface NoteCreatedMetadata {
  note: string;
}

export interface TaskCreatedMetadata {
  task_id: string;
  task_type: 'call' | 'email' | 'meeting' | 'other';
  due_date: string;
  title: string;
}

export interface TaskCompletedMetadata {
  task_id: string;
  completed_at: string;
}

export interface TaskRescheduledMetadata {
  task_id: string;
  days_added: number;
  new_due_date: string;
}

export interface SequenceSentMetadata {
  sequence_name: string;
  step_number: number;
  subject: string;
  theme: string;
}

export interface DataUpdatedMetadata {
  fields_changed: Array<{
    field: string;
    old_value: any;
    new_value: any;
  }>;
  summary: string;
}

export interface PaymentPaidMetadata {
  payment_case_id: string;
  comment: string;
  confirmed_by: string;
}

export interface PaymentMethodSelectedMetadata {
  payment_case_id: string;
  method: string;
  order_id: string;
}

// =====================================================
// INTERFACES PRINCIPALES
// =====================================================

export interface TimelineEvent {
  id: string;
  entity_type: EntityType;
  entity_id: string;
  event_type: TimelineEventType;
  title: string;
  description: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  created_by: string | null;
  // Relations (chargées optionnellement)
  created_by_user?: {
    id: string;
    full_name: string | null;
    email: string;
  } | null;
}

export interface CreateTimelineEventDto {
  entity_type: EntityType;
  entity_id: string;
  event_type: TimelineEventType;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
}

// =====================================================
// LABELS POUR L'AFFICHAGE
// =====================================================

export const TIMELINE_EVENT_TYPE_LABELS: Record<TimelineEventType, string> = {
  email_sent: 'Email envoyé',
  email_received: 'Email reçu',
  email_bounced: 'Email non délivré',
  call_logged: 'Appel consigné',
  status_changed: 'Statut modifié',
  assigned_user_changed: 'Assignation modifiée',
  field_updated: 'Champ mis à jour',
  note_created: 'Note ajoutée',
  entity_created: 'Création',
  task_created: 'Tâche créée',
  task_completed: 'Tâche terminée',
  task_rescheduled: 'Tâche reportée',
  sequence_sent: 'Séquence envoyée',
  data_updated: 'Données modifiées',
  crew_member_added: 'Membre ajouté',
  crew_member_removed: 'Membre retiré',
  payment_initiated: 'Paiement initié',
  payment_method_selected: 'Méthode de paiement choisie',
  payment_method_changed: 'Méthode de paiement modifiée',
  payment_paid: 'Paiement confirmé',
  payment_failed: 'Paiement échoué',
  payment_attempt_untracked: 'Paiement CB non rattaché (à vérifier)',
  payment_payer_identified: 'Payeur identifié',
  payment_payer_changed: 'Payeur modifié',
  payment_reminder_sent: 'Relance paiement envoyée',
  payment_link_resent: 'Mail de paiement renvoyé',
  bpi_manually_validated: 'BPI validé manuellement',
  bed_link_generated: 'Lien BED généré',
  bed_link_expired: 'Lien BED expiré',
  bed_link_sent: 'Lien BED envoyé',
  bed_options_updated: 'Options BED mises à jour',
  bed_amount_adjusted: 'Montant du solde BED ajusté',
  bed_amount_alert: 'Solde BED à vérifier',
  payment_refund_recorded: 'Remboursement de trop-perçu noté',
  bed_form_link_generated: 'Lien du formulaire BED généré',
  bed_missing_item_resolved: 'Élément manquant levé',
  bed_resubmitted: 'Bulletin BED mis à jour par le participant',
  bed_certificate_reviewed: 'Certificat médical examiné',
  bed_custom_item_added: 'Élément manquant ajouté',
  bed_custom_item_removed: 'Élément manquant retiré',
  bed_reminder_queued: 'Relance BED mise en file',
  bed_vehicle_attached: 'Véhicule rattaché depuis le bulletin',
  bed_vehicle_refused: 'Véhicule du bulletin non repris',
};

export const CALL_DIRECTION_LABELS: Record<CallDirection, string> = {
  incoming: 'Entrant',
  outgoing: 'Sortant',
};

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  answered: 'Répondu',
  voicemail: 'Répondeur',
  no_answer: 'Non abouti',
};

// =====================================================
// COULEURS PAR TYPE D'ÉVÉNEMENT
// =====================================================

export const TIMELINE_EVENT_COLORS: Record<
  TimelineEventType,
  { bg: string; text: string; border: string }
> = {
  call_logged: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-500',
  },
  email_sent: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-500',
  },
  email_received: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-500',
  },
  email_bounced: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-500',
  },
  note_created: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-500',
  },
  status_changed: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-500',
  },
  assigned_user_changed: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-500',
  },
  field_updated: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-500',
  },
  entity_created: {
    bg: 'bg-zinc-50',
    text: 'text-zinc-700',
    border: 'border-zinc-400',
  },
  task_created: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-500',
  },
  task_completed: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-500',
  },
  task_rescheduled: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-500',
  },
  sequence_sent: {
    bg: 'bg-[#fef9c2]',
    text: 'text-yellow-900',
    border: 'border-yellow-400',
  },
  data_updated: {
    bg: 'bg-[#ffedd4]',
    text: 'text-orange-900',
    border: 'border-orange-400',
  },
  crew_member_added: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
  },
  crew_member_removed: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-500',
  },
  payment_initiated: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-500',
  },
  payment_method_selected: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-500',
  },
  payment_method_changed: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-500',
  },
  payment_paid: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-500',
  },
  payment_failed: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-500',
  },
  payment_attempt_untracked: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-500',
  },
  payment_payer_identified: {
    bg: 'bg-cyan-50',
    text: 'text-cyan-700',
    border: 'border-cyan-500',
  },
  payment_payer_changed: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-500',
  },
  payment_reminder_sent: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-500',
  },
  payment_link_resent: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-500',
  },
  bpi_manually_validated: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
  },
  bed_link_generated: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-500',
  },
  bed_link_expired: {
    bg: 'bg-zinc-50',
    text: 'text-zinc-700',
    border: 'border-zinc-400',
  },
  bed_link_sent: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-500',
  },
  bed_form_link_generated: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-500',
  },
  bed_missing_item_resolved: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
  },
  bed_resubmitted: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
  },
  bed_certificate_reviewed: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
  },
  bed_custom_item_added: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
  },
  bed_custom_item_removed: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
  },
  bed_reminder_queued: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
  },
  bed_vehicle_attached: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
  },
  bed_vehicle_refused: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-500',
  },
  bed_options_updated: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-500',
  },
  bed_amount_adjusted: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-500',
  },
  bed_amount_alert: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-500',
  },
  payment_refund_recorded: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-500',
  },
};
